/**
 * What an app's page says to the person who asked for the app (LO-207), in the
 * words of somebody who does not build apps, without React or a network, so each
 * sentence can be read and tested on its own.
 *
 * ## What this is, and what it is not
 *
 * The page used to be written for a reviewer: *persona*, *synthetic data*, *seed
 * data*, *instance*, *QA run*, *kernel calls*, *egress* and the id of each check,
 * with "skipped, not a pass" repeated under every persona. An author wants three
 * things from it, in this order: where is my app, what do I do next, and did it
 * work. So the words here are the author's: **test user**, **made-up data**,
 * **test copy**, **checks**, **outside sites**, and the Kernel by name.
 *
 * Nothing is dropped. Every technical word and every kernel sentence this module
 * leaves out still has a place, under "For reviewers" on the page, and what this
 * module says it derives from the same fields the old page drew: the request, the
 * QA report and its gate, the call log and the egress log. It decides **presentation**
 * and never a rule: whether a run passed is the kernel's (`gate.passed_for_current`,
 * read through `headline` and `submitGate`), and a check that is in no group below is
 * shown under "Other checks" with the title the kernel gave it, so a check the kernel
 * adds appears with no change here.
 *
 * ## What a pass does not say
 *
 * A pass is only ever said with what it was a pass of (the test users, made-up
 * data), and what it left unchecked is said on the line it qualifies: a topic that
 * passed with part of it unchecked says so on that row, and a topic nothing could
 * check says it was **not tested** and why, never "passed".
 */

import { headline, inconclusiveChecks, runOf } from "./sandboxPanel.js";

/** Words that belong under "For reviewers" and nowhere an author reads first. The
 *  tests hold every sentence below to this list. */
export const REVIEWER_WORDS = /\b(personas?|synthetic|seed data|instances?|QA|qa run|kernel calls?|egress|gateway|check ids?|manifest|bounded crawl|provision(?:ing|ed)?|control plane|fixtures?)\b/i;

// -- the app's name ----------------------------------------------------------------

/** `app-math-runner` as `Math runner`: the slug is the only name the app has, so a
 *  name an author reads is the slug without its prefix, and the slug stays beneath it. */
export function appTitle(slug) {
  const words = String(slug || "").replace(/^app-/, "").split(/[-_]+/).filter(Boolean);
  if (!words.length) return String(slug || "");
  const text = words.join(" ");
  return `${text[0].toUpperCase()}${text.slice(1)}`;
}

// -- time --------------------------------------------------------------------------

/** `5 minutes ago`, from an ISO time and now (milliseconds), or nothing for a time that
 *  is not one. A time in the future reads as just now rather than as a negative. */
export function ago(iso, now = Date.now()) {
  if (!iso) return "";
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return "";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

// -- the test users ----------------------------------------------------------------

/** The names of the people the test copy can be tried as: what the draft declares,
 *  else the people the checks ran as, else nobody is named. */
export function testUsersOf(request, run) {
  const declared = (request?.proposed?.personas || []).map((p) => String(p?.name || "")).filter(Boolean);
  if (declared.length) return declared;
  return (run?.personas || []).map((p) => String(p?.name || "")).filter(Boolean);
}

/** Who you will be, in a sentence. The made-up data is said here, where the person is
 *  about to meet it, and again in the test copy itself, which says so on every screen. */
export function signInLine(users) {
  if (!users.length) {
    return "The test copy will ask which test user you want to be. It uses made-up data, not your real records.";
  }
  // The names follow as chips, so the sentence does not list them twice.
  if (users.length === 1) {
    return "You'll be this test user when it opens. It uses made-up data, not your real records.";
  }
  return "You'll pick one of these test users when it opens. It uses made-up data, not your real records.";
}

/** `the test user`, `both test users`, `all 3 test users`, or the whole app when nobody
 *  was named: the people a result is for. */
export function usersWords(n) {
  if (!n) return "the app";
  if (n === 1) return "the test user";
  if (n === 2) return "both test users";
  return `all ${n} test users`;
}

// -- where the app is, and what to do next -----------------------------------------

/**
 * Which of the situations an app can be in, from plain facts. One name for each, so
 * the sentence, the next step and the progress line cannot disagree about it.
 *
 *  - `state` is the request's own: draft, pending, approved, rejected, cancelled,
 *    or none; `box` is the test copy's (`sandboxState`); `tone` is `headline(qa).tone`;
 *  - `revising` is the builder at work on a change since the draft was saved (LO-206);
 *  - `working` is a chat about this app being at work.
 *
 * A draft that is being revised is the revision's story and nothing else: the checks
 * below it are the previous version's, and the sentence says so before it says anything
 * about them.
 */
export function situationOf({ loading, unreadable, state, liveReady, liveVersion, box, qaUnreadable, tone, revising, working }) {
  if (loading) return "loading";
  if (unreadable) return "unreadable";
  if (!state) {
    if (liveReady || liveVersion) return working ? "live_changing" : "live";
    return working ? "building" : "none";
  }
  if (state === "draft") {
    if (revising) return "revising";
    const where = box || "none";
    if (where === "failed") return "box_failed";
    if (where === "stale") return "box_stale";
    if (where === "unknown") return "box_unknown";
    if (where !== "ready") return "box_wait";
    if (qaUnreadable) return "qa_unreadable";
    return {
      none: "ready_norun", running: "checking", passed: "passed", stale: "passed_stale",
      failed: "failed", inconclusive: "inconclusive", error: "error",
    }[tone] || "ready_norun";
  }
  if (state === "pending") return "pending";
  if (state === "approved") {
    if (!liveReady) return "installing";
    return working ? "live_changing" : "live";
  }
  if (state === "rejected") return "rejected";
  if (state === "cancelled") return "cancelled";
  return "closed";
}

const MARK = {
  good: { glyph: "✓", tone: "success" },
  bad: { glyph: "✕", tone: "error" },
  attention: { glyph: "!", tone: "warning" },
  going: { glyph: "↻", tone: "info" },
  waiting: { glyph: "…", tone: "info" },
  plain: { glyph: "○", tone: "default" },
};

/**
 * The one sentence under the app's name, with the marker that goes with it. The marker
 * is a glyph and the sentence says the same thing in words: neither is the colour alone.
 *
 *  `facts`: `version` (the live version), `ago` (how long ago the run finished), `sent`
 *  (how long ago it was sent for approval), `reason` (a governor's, as given), `box`
 *  (the test copy's state), `recovery` (what the provisioner recorded when it was late).
 */
export function statusOf(situation, facts = {}) {
  const when = facts.ago ? ` ${facts.ago}` : "";
  const sentence = {
    loading: [MARK.plain, "Reading the latest status…"],
    unreadable: [MARK.attention, "The status couldn't be read just now. Check again in a moment."],
    none: [MARK.plain, "There is no draft yet. Ask for a change, or continue the last chat, and the builder will make one."],
    building: [MARK.going, "The builder is working on the first version."],
    revising: [MARK.going, "A revision is being built. What you see here is the previous version."],
    // A copy that stalled and is being replaced is true now and bad, so it is said at full size.
    box_wait: [MARK.waiting, ["stalled", "replaced"].includes(facts.recovery)
      ? "The test copy stalled while it was coming up, and it's being replaced. That takes about ten minutes."
      : facts.box === "none"
        ? "The test copy hasn't been made yet. It starts when the builder saves a draft and takes about ten minutes."
        : "Getting the test copy ready. It takes about ten minutes."],
    box_failed: [MARK.bad, "The test copy couldn't be made. Ask for a change and the builder will save a new draft."],
    box_stale: [MARK.attention, "The test copy is for an older version of this draft, so it can't be tried or checked."],
    box_unknown: [MARK.attention, "Whether the test copy is running couldn't be read just now."],
    qa_unreadable: [MARK.attention, "How the checks went couldn't be read just now."],
    ready_norun: [MARK.plain, "The test copy is ready. The checks haven't been run yet."],
    checking: [MARK.going, "The checks are running. This takes a few minutes."],
    passed: [MARK.good, `Ready to send for approval: it passed its checks${when}.`],
    passed_stale: [MARK.attention, "It passed for earlier permissions, so run the checks again before sending it."],
    failed: [MARK.bad, "Not ready: it didn't pass its checks. Ask for a change to fix what failed."],
    inconclusive: [MARK.attention, "The checks couldn't finish judging it. That isn't a finding about the app: run them again."],
    error: [MARK.attention, "The checks didn't finish. That isn't a finding about the app: run them again."],
    pending: [MARK.waiting, `Waiting for a governor to approve it${facts.sent ? ` (sent ${facts.sent})` : ""}.`],
    installing: [MARK.waiting, "Approved. It's being installed, and the link appears here when it's ready."],
    live: [MARK.good, facts.version ? `Live: version ${facts.version} is running.` : "Live."],
    live_changing: [MARK.going, "Live, and a change is being built."],
    // A reason is a governor's own sentence and usually ends its own full stop.
    rejected: [MARK.bad, `A governor turned it down${facts.reason ? `: ${String(facts.reason).trim().replace(/[.!?]+$/, "")}` : ""}. Ask for a change and the builder will send a new request.`],
    cancelled: [MARK.attention, "The last request was cancelled. Ask for a change to send a new one."],
    closed: [MARK.attention, "The last request was replaced. Ask for a change to send a new one."],
  }[situation] || [MARK.plain, ""];
  const [mark, text] = sentence;
  return { ...mark, text };
}

/**
 * The single next thing, with the button that does it. `kind` is what the page does when
 * it is pressed; the page owns how. `why` is set when the thing cannot be done yet, in
 * the words of `gateWords`.
 *
 *  `facts`: `can` (Send for approval is available), `why` (the plain reason it is not),
 *  `hasLink` (the test copy can be opened), `hasChat` (there is a chat to go to).
 */
export function nextStepOf(situation, facts = {}) {
  const ask = { kind: "ask", label: "Ask for a change" };
  const again = { kind: "refresh", label: "Check again" };
  const chat = facts.hasChat ? { kind: "chat", label: "Watch it in the chat" } : null;
  const step = {
    loading: null,
    unreadable: ["Check again", "Nothing could be read from the Kernel just now, so there is nothing to act on yet.", again],
    none: ["Ask for a change", "Say what the app should do or change, and the builder makes a draft of it.", facts.hasChat ? { kind: "continue", label: "Continue the last chat" } : ask],
    building: ["Wait for the builder", "It's making the first version. A draft appears here when it's saved.", chat],
    revising: ["Wait for the new version", "The builder is making the change you asked for. Sending is held until it saves the new draft and that draft's checks pass.", chat],
    box_wait: ["Wait for the test copy", "It takes about ten minutes. This page updates itself.", again],
    box_failed: ["Ask for a change", "Ask the builder to save the draft again and it will make a new test copy.", ask],
    box_stale: ["Ask for a change", "Ask the builder to save the draft again so there is a test copy for it.", ask],
    box_unknown: ["Check again", "The page will try to read the test copy again.", again],
    qa_unreadable: ["Check again", "The page will try to read the checks again.", again],
    ready_norun: ["Run the checks", "They try the test copy as each test user, and have to pass before it can be sent for approval.", { kind: "run", label: "Run the checks now" }],
    checking: ["Wait for the checks", "They take a few minutes. This page updates itself.", again],
    passed: ["Send it for approval", "A governor decides whether it goes live. Sending it doesn't approve anything.", { kind: "submit", label: "Send for approval" }],
    passed_stale: ["Run the checks again", "The last pass was for earlier permissions, so it doesn't count for this draft.", { kind: "run", label: "Run the checks again" }],
    failed: ["Fix what failed", "Ask for a change and say what to fix. The builder saves a new draft, and the checks run again on it.", ask],
    inconclusive: ["Run the checks again", "The platform couldn't judge part of it, which isn't a problem with the app.", { kind: "run", label: "Run the checks again" }],
    error: ["Run the checks again", "They didn't finish, which isn't a problem with the app.", { kind: "run", label: "Run the checks again" }],
    pending: ["Wait for a governor", "A governor approves it on the console's Requests screen. The builder can't approve its own work.", { kind: "requests", label: "Open Requests in the console" }],
    installing: ["Wait for it to install", "It was approved. The link appears when it's ready, usually within a few minutes.", { kind: "reload", label: "Check again" }],
    live: ["Nothing to do", "It's live. To update it, ask for a change: it is built, then sent for approval like the first version.", ask],
    live_changing: ["A change is being built", "It replaces what is live only after a governor approves it.", chat],
    rejected: ["Ask for a change", "Tell the builder what to change and it will send a new request.", ask],
    cancelled: ["Ask for a change", "A new request is made once the builder has a draft.", ask],
    closed: ["Ask for a change", "A new request is made once the builder has a draft.", ask],
  }[situation];
  if (!step) return null;
  const [title, text, action] = step;
  return {
    title,
    text,
    action,
    // Only the one thing that can be unavailable has a reason of its own.
    why: action?.kind === "submit" && facts.can === false ? facts.why || "" : "",
    can: action?.kind === "submit" ? facts.can !== false : true,
  };
}

/**
 * Why Send for approval is not available, in plain words, from the gate's `kind`
 * (`submitGate`). The kernel's own sentence is kept whole under "For reviewers", where a
 * reviewer reads it; this is the one an author acts on.
 */
export function gateWords(gate, box) {
  const kind = gate?.kind;
  const boxWait = box && box.state && box.state !== "ready"
    ? " The test copy isn't ready yet, and the checks start once it is."
    : "";
  return {
    loading: "Still reading the latest status.",
    sent: "It has already been sent. A governor decides it.",
    closed: "This request is closed, so there is nothing to send.",
    revising: "A revision is being built. Sending is held until the builder saves the new draft and its checks pass.",
    unreadable: "How the checks went couldn't be read just now.",
    no_run: `The checks haven't passed yet. Run them first.${boxWait}`,
    stale_run: "The checks passed for earlier permissions. Run them again.",
    running: "The checks are still running.",
    failed: "The checks didn't pass. Ask for a change to fix what failed.",
    inconclusive: "The checks couldn't finish judging it. Run them again.",
    error: "The checks didn't finish. Run them again.",
    passed: "",
  }[kind] ?? (gate?.can ? "" : "It can't be sent yet.");
}

/**
 * Why Send for approval is not available, by where the app is first and by the gate second: an
 * app that is live, or has no draft, has nothing to send, and "the request is closed" is the
 * kernel's way of saying it, which is not an author's.
 */
export function sendWhy(situation, gate, box) {
  switch (situation) {
    case "loading": return "Still reading the latest status.";
    case "unreadable": return "The status couldn't be read just now.";
    case "pending": return "It has already been sent. A governor decides it.";
    case "installing": case "live": case "live_changing":
      return "It's already approved. Ask for a change to send a new version.";
    case "none": case "building": case "rejected": case "cancelled": case "closed":
      return "There is no draft to send. Ask for a change and the builder makes one.";
    default: return gateWords(gate, box);
  }
}

// -- the progress line -------------------------------------------------------------

export const JOURNEY = [
  { key: "built", label: "Built" },
  { key: "tried", label: "Tried out" },
  { key: "sent", label: "Sent for approval" },
  { key: "approved", label: "Approved" },
  { key: "live", label: "Live" },
];

/** What a step is called when it is read aloud and when it is read with no colour. */
export const JOURNEY_WORD = { done: "done", current: "now", todo: "not yet", stuck: "needs attention" };

/**
 * Where the app is on the five steps. A step is **done** only when something says so: the
 * checks passing (the kernel's gate), the request being pending, approved by a governor, or
 * an install being ready. Approved and Live are never marked on the strength of anything
 * else, which is how the progress read before.
 */
export function journeyOf(situation) {
  const order = JOURNEY.map((s) => s.key);
  const at = (done, current, stuck) => order.map((key, i) => ({
    ...JOURNEY[i],
    state: stuck === i ? "stuck" : i < done ? "done" : i === current ? "current" : "todo",
  }));
  switch (situation) {
    case "loading": case "unreadable": return at(0, -1);
    // A revision is a version being built: the previous one was built, and this is not it.
    case "none": case "building": case "revising": return at(0, 0);
    case "box_wait": case "box_unknown": case "qa_unreadable":
    case "ready_norun": case "checking": case "passed_stale": return at(1, 1);
    // Tried out is where it is stuck: the test copy, or the checks, need somebody.
    case "box_failed": case "box_stale": case "failed": case "inconclusive": case "error": return at(1, 1, 1);
    case "passed": return at(2, 2);
    case "pending": return at(3, 3);
    case "rejected": return at(3, 3, 3);
    case "installing": return at(4, 4);
    case "live": case "live_changing": return order.map((key, i) => ({ ...JOURNEY[i], state: "done" }));
    case "cancelled": case "closed": return at(1, -1);
    default: return at(0, -1);
  }
}

// -- how it did in testing ---------------------------------------------------------

/**
 * What the checks looked at, in plain words. Each topic is a set of the kernel's check
 * ids; the ids are the report's contract and a topic names no id. A check in none of them
 * is shown under "Other checks", by the title the kernel gave it.
 *
 * `untested` is what it means when nothing under a topic could be checked: the reason, in
 * words, why there was nothing to look at.
 */
export const TOPICS = [
  {
    key: "works", label: "The app opens and works",
    ids: ["sign_in", "banner", "explored", "console_clean", "dom_props_clean", "time_budget", "request_unchanged"],
    untested: "the checker could not get far enough into the app to look",
  },
  {
    key: "data", label: "Each test user sees the right data",
    ids: ["reads_outside_ceiling_refused", "no_call_outside_request", "no_forbidden_write"],
    untested: "the app made no calls the checker could judge",
  },
  {
    key: "access", label: "A test user who can't open the app is told why",
    ids: ["missing_permissions_screen"],
    untested: "the Kernel couldn't say which test users could open the app",
  },
  {
    key: "refusals", label: "Refusals are shown as refusals",
    ids: ["refusal_rendered_as_refusal", "refusals_matched_by_log"],
    untested: "nothing was refused, so there was no refusal to look at",
  },
  {
    key: "saved", label: "Anything it saved is recorded",
    ids: ["writes_confirmed_by_log"],
    untested: "this app never saved anything, so there was nothing to check",
  },
  {
    key: "usable", label: "It's easy to use: keyboard, colours, screen size",
    ids: ["keyboard_reach", "focus_visible", "colour_schemes", "no_horizontal_scroll"],
    untested: "there was nothing on screen to judge",
  },
  {
    key: "outside", label: "It only contacts the outside sites it asked for",
    ids: ["browser_egress", "egress_log"],
    untested: "there was nothing to check",
  },
  {
    key: "rules", label: "It follows the Kernel's design and governance rules",
    ids: ["explains_why", "no_second_org", "static_review", "governance"],
    untested: "there was nothing to check",
  },
];

/** The word on a row, and what it says. A pass with part of it unchecked is still `passed`
 *  and says so in its detail, on the row. */
export const TOPIC_WORD = { passed: "Passed", failed: "Failed", unjudged: "Couldn't be judged", untested: "Not tested" };

/**
 * A failing check in words an author can say back to the builder. The kernel's own title
 * and detail for it are in the report under "For reviewers" and are what the builder reads
 * with its own tools; a check the kernel adds that is not here is shown by the title it gave.
 */
export const PLAIN_TITLE = {
  sign_in: "Signing in as the test user works",
  banner: "The test copy's banner shows who you are",
  explored: "The checker found screens with something on them",
  missing_permissions_screen: "A test user who can't open the app is shown a screen that says why, and none of the app",
  refusal_rendered_as_refusal: "A refusal is shown as a refusal, not as an empty page",
  explains_why: "Each screen says why you see what you see",
  no_second_org: "Nothing suggests another organisation exists",
  keyboard_reach: "Everything can be reached with the keyboard",
  focus_visible: "A selected control looks selected",
  colour_schemes: "Light and dark colours both work",
  console_clean: "No errors show up behind the scenes",
  dom_props_clean: "No broken layout settings in the page",
  no_horizontal_scroll: "The page doesn't scroll sideways",
  no_forbidden_write: "It doesn't try to change something it isn't allowed to",
  reads_outside_ceiling_refused: "Asking for data it wasn't granted is refused",
  writes_confirmed_by_log: "Every change it made is recorded",
  refusals_matched_by_log: "Every refusal it saw is recorded",
  no_call_outside_request: "It only asks for the data it said it needs",
  browser_egress: "The page only contacts the outside sites it asked for",
  time_budget: "It finishes within the time allowed",
  static_review: "Its code follows the design rules",
  governance: "Its code follows the governance rules",
  egress_log: "The app's server only contacts the outside sites it asked for",
  request_unchanged: "The request didn't change while the checks ran",
};

const every = (run) => [
  ...(run?.checks || []).map((c) => ({ ...c, user: "" })),
  ...(run?.personas || []).flatMap((p) => (p.checks || []).map((c) => ({ ...c, user: p.name }))),
];

const where = (c) => [c.user ? `as ${c.user}` : "for the whole app", c.route ? `at ${c.route}` : ""].filter(Boolean).join(" ");

function rowOf(group) {
  const failed = group.checks.filter((c) => c.status === "fail" && c.inconclusive !== true);
  const unjudged = group.checks.filter((c) => c.status === "fail" && c.inconclusive === true);
  const passed = group.checks.filter((c) => c.status === "pass");
  // Anything that is neither a pass nor a failure is not a pass.
  const skipped = group.checks.length - failed.length - unjudged.length - passed.length;
  const base = { key: group.key, label: group.label };
  if (failed.length) {
    return {
      ...base, status: "failed", detail: "",
      fixes: failed.slice(0, 4).map((c) => ({
        id: c.id, title: PLAIN_TITLE[c.id] || c.title || c.id, where: where(c),
      })),
    };
  }
  if (unjudged.length) {
    return {
      ...base, status: "unjudged",
      detail: "The platform couldn't judge this, which isn't a finding about the app. Run the checks again.",
      fixes: [],
    };
  }
  if (!passed.length) return { ...base, status: "untested", detail: `Nothing was checked: ${group.untested}.`, fixes: [] };
  return {
    ...base, status: "passed", fixes: [],
    detail: skipped
      ? `${skipped} of its ${group.checks.length} ${group.checks.length === 1 ? "check" : "checks"} had nothing to look at: ${group.untested}.`
      : "",
  };
}

const listOf = (names) => (names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

/**
 * The row for the screen a test user who can't open the app is shown (LO-211), which says what it was
 * a pass **of**. Every test user is judged by that check, and for the ones who can open the app a pass
 * only says no screen was shown to them; so a run in which nobody was missing a tag has not tried the
 * screen at all, and is said to be not tested and never worded as a pass. Who was missing a tag is the
 * kernel's answer to each test user, carried on the report (`access.has_access`).
 */
function accessRow(run, row) {
  if (row.status !== "passed") return row;
  const missing = (run?.personas || []).filter((p) => p?.access?.has_access === false).map((p) => String(p.name));
  if (!missing.length) {
    return {
      ...row, status: "untested",
      detail: "No test user was missing a tag, so the screen that tells somebody what they lack wasn't tried. Add a test user who is missing one.",
    };
  }
  return {
    ...row,
    detail: `${listOf(missing)} ${missing.length === 1 ? "is" : "are"} missing a tag the app needs. They were shown the screen that says what they lack, and none of the app.`,
  };
}

/** The rows under "How it did in testing", in order, one per topic that has any check. */
export function topicsOf(run) {
  if (!run) return [];
  const all = every(run);
  const known = new Set(TOPICS.flatMap((t) => t.ids));
  const groups = TOPICS.map((t) => ({ ...t, checks: all.filter((c) => t.ids.includes(c.id)) }));
  const rest = all.filter((c) => !known.has(c.id));
  if (rest.length) groups.push({ key: "other", label: "Other checks", untested: "there was nothing to check", checks: rest });
  return groups.filter((g) => g.checks.length).map((g) => (g.key === "access" ? accessRow(run, rowOf(g)) : rowOf(g)));
}

/** What to put in the change box when somebody asks the builder to fix a row: the failing
 *  check in words, and who it failed for, so nothing is typed out again. */
export function fixRequest(row) {
  const lines = (row?.fixes || []).map((f) => `- ${f.title} (${f.where})`);
  return [`Please fix this: ${row?.label || "a check that failed"}.`, ...lines].join("\n");
}

/** How many checks had nothing to look at, across the whole run. */
export function skippedCount(run) {
  return every(run).filter((c) => c.status !== "pass" && c.status !== "fail").length;
}

/**
 * The verdict in one line: a word, a marker and the rest of the sentence, which says what the
 * pass or the failure was about. The page sets them as `Passed: every check ran for …`, so
 * `text` carries no word of its own. The kernel decides which it is (`headline`); this words it.
 */
export function verdictOf({ qa, users = 0, now = Date.now() }) {
  const run = runOf(qa);
  const view = headline(qa);
  const who = usersWords(users);
  const when = run?.finished_at ? ago(run.finished_at, now) : "";
  if (!run) {
    return { ...MARK.plain, kind: view.tone, word: "Not checked yet", text: "the checks haven't been run on this test copy.", when };
  }
  if (view.tone === "running") {
    return { ...MARK.going, kind: "running", word: "Checking", text: "the checks are running. This takes a few minutes.", when };
  }
  if (view.tone === "passed") {
    const skipped = skippedCount(run);
    return {
      ...MARK.good, kind: "passed", word: "Passed",
      text: skipped
        ? `nothing went wrong for ${who}, on made-up data. ${skipped} ${skipped === 1 ? "check" : "checks"} had nothing to look at.`
        : `every check ran for ${who}, and nothing went wrong, on made-up data.`,
      when,
    };
  }
  if (view.tone === "stale") {
    return { ...MARK.attention, kind: "stale", word: "Out of date", text: "it passed, but for earlier permissions, so that doesn't count for this draft. Run the checks again.", when };
  }
  if (view.tone === "inconclusive") {
    const n = inconclusiveChecks(run).length;
    return {
      ...MARK.attention, kind: "inconclusive", word: "Couldn't be judged",
      text: `the platform couldn't judge ${n === 1 ? "one check" : `${n} checks`}. That isn't a finding about the app, and it isn't a pass: run the checks again.`,
      when,
    };
  }
  if (view.tone === "error") {
    return { ...MARK.attention, kind: "error", word: "Didn't finish", text: "the checks stopped before they were done. That isn't a finding about the app, and it isn't a pass.", when };
  }
  if (view.tone === "failed") {
    const failing = (run.personas || []).filter((p) => p.status === "failed" || p.status === "error").map((p) => p.name);
    const n = run.summary?.failed ?? 0;
    return {
      ...MARK.bad, kind: "failed", word: "Didn't pass",
      text: `${n ? `${n} ${n === 1 ? "check" : "checks"} failed` : "it failed"}${failing.length ? ` for ${failing.join(", ")}` : ""}.`,
      when,
    };
  }
  return { ...MARK.plain, kind: view.tone, word: view.word, text: view.text, when };
}

/** What this does not show, for the disclosure on the verdict: the kernel's own sentences
 *  follow it, and nothing here stands in for them. */
export const DOESNT_SHOW = "It covers what the checker looked at, as these test users, on made-up data. It isn't a safety claim.";

/** The note under a verdict when the checker did not open the app at all, which is not a pass. */
export function liveRunNote(run) {
  const live = run?.live_run;
  if (!live || live.ran !== false) return "";
  const why = String(live.skipped_reason || "").replace(/_/g, " ");
  return `The checker didn't open the app this time${why ? ` (${why})` : ""}, so nothing was explored. That is not a pass.`;
}

// -- what it asks to be allowed to do ----------------------------------------------

/** An action as the request carries it: a verb, or (as `GET /manifest-requests/{id}` serves it, with
 *  what the verb does beside it) an object holding the verb. Printing the object is how an app's page
 *  once said "Do [object Object]". */
const verbOf = (action) => (action && typeof action === "object" ? String(action.verb ?? action.name ?? "") : String(action));

/**
 * What the draft asks for, as sentences, from its own request. The first four mirror what
 * the chat page says (`proposal_in_words` in the Builder's function) word for word, so the
 * two pages cannot tell different stories about one draft; the outside sites are added here
 * because they are part of what is approved and the chat's sentences do not carry them.
 * Tags are shown as the request has them: the kernel has already refused one it does not know.
 */
export function allowedLines(proposed) {
  const p = proposed || {};
  const lines = [];
  for (const row of p.reads || []) {
    if (!row || typeof row !== "object") continue;
    const tags = row.tags || [];
    const scope = tags.length ? `only those tagged ${tags.join(", ")}` : "only untagged ones";
    lines.push(`Read ${row.type} records — ${scope}, and only those the person using it may already see.`);
  }
  for (const action of p.actions || []) lines.push(`Do ${verbOf(action)}, as the person using it.`);
  for (const row of p.service_reads || []) {
    if (row && typeof row === "object") lines.push(`Read ${row.type} with nobody signed in (an unattended agent).`);
  }
  for (const action of p.service_actions || []) lines.push(`Do ${verbOf(action)} with nobody signed in.`);
  if (!lines.length) lines.push("Nothing: it reads no records and writes none.");
  const hosts = p.egress || [];
  lines.push(hosts.length ? `Contact these outside sites: ${hosts.join(", ")}.` : "Contact no outside sites.");
  return lines;
}

// -- the folded detail -------------------------------------------------------------

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The test settings in one line, for the disclosure while it is closed. */
export function settingsLine(proposed) {
  const p = proposed || {};
  const users = (p.personas || []).length;
  const records = Object.values(p.fixtures || {}).reduce((n, rows) => n + (rows || []).length, 0);
  const hosts = (p.egress || []).length;
  return `${plural(users, "test user", "test users")}, ${plural(records, "sample record", "sample records")}, ${hosts ? plural(hosts, "outside site", "outside sites") : "no outside sites"} the app may contact.`;
}

/**
 * The reviewers' detail in one line, for the disclosure while it is closed. Each figure
 * carries what it is of: the calls are the Kernel's count of calls the test copy made, and
 * an outside-sites reading carries the log's own limit, which is that it can miss an attempt.
 * A log that has not been read says nothing, and is not worded as none.
 */
export function reviewersLine({ users = 0, calls, egress, reading }) {
  const parts = [];
  if (users) parts.push(`Results for ${plural(users, "test user", "test users")}`);
  const s = calls?.summary;
  if (s) {
    parts.push(s.total
      ? `${s.total} ${s.total === 1 ? "call" : "calls"} to the Kernel: ${s.allowed} allowed${s.empty ? `, ${s.empty} empty` : ""}, ${s.refused} refused`
      : "no calls to the Kernel logged yet");
  }
  if (egress) {
    const hosts = new Set((egress.hosts || []).map((h) => h.host)).size;
    if (reading === "not_recorded") parts.push("outside sites not recorded");
    else if (reading === "nothing_contacted") parts.push("no outside site tried (the log can miss an attempt)");
    else if (hosts) parts.push(`${plural(hosts, "outside site", "outside sites")} tried`);
    else parts.push("outside sites unknown");
  }
  return parts.length ? `${parts.join(" · ")}.` : "The kernel's own sentences, each test user's results, the calls and the outside sites tried.";
}

// -- the test copy -----------------------------------------------------------------

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** One line: how long it has been up and what it is estimated to cost, called an estimate. */
export function copyLine(upkeep) {
  const e = upkeep?.estimate;
  if (!e) return null;
  const days = Math.max(0, Math.floor(Number(upkeep.age_days) || 0));
  const age = days === 0 ? "Running for less than a day" : `Running for ${plural(days, "day", "days")}`;
  return { text: `${age} · about ${money(e.monthly_usd)} a month (an estimate)`, title: e.basis || "" };
}

/** What the "?" says: what the estimate is, when a reminder comes, and what ends the test copy.
 *  The figures are the kernel's (`idle_chase_after_days`); the sentences are plain. */
export function copyHelp(upkeep) {
  const after = Math.max(0, Math.floor(Number(upkeep?.idle_chase_after_days) || 0));
  return [
    "The cost is an estimate from list prices for what the test copy is made of. It isn't a bill.",
    after
      ? `If nothing happens to it for ${plural(after, "day", "days")} you get a reminder by email, and another after ${plural(after * 2, "day", "days")}. It is never removed on a timer: sending the draft for approval, or cancelling it, ends the test copy.`
      : "It is never removed on a timer: sending the draft for approval, or cancelling it, ends the test copy.",
    "The email switch changes only your own emails, for every draft you make.",
  ];
}
